import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Users } from "lucide-react";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, EmptyState, Spinner } from "@/components/ui-bits";
import { ExpertCard } from "@/components/ExpertCard";
import { Input } from "@/components/ui/input";
import { FIELDS } from "@/lib/constants";

export default function ExpertDirectory() {
  const { t } = useTranslation();
  const [experts, setExperts] = useState(null);
  const [q, setQ] = useState("");
  const [field, setField] = useState("all");

  useEffect(() => {
    api.get("/experts", { params: { scope: "network" } }).then(({ data }) => setExperts(data)).catch(() => setExperts([]));
  }, []);

  const filtered = useMemo(() => {
    if (!experts) return [];
    const s = q.toLowerCase();
    return experts.filter((e) =>
      (field === "all" || (e.fields || []).includes(field)) &&
      (!s || e.name.toLowerCase().includes(s) || (e.title || "").toLowerCase().includes(s) || (e.headline || "").toLowerCase().includes(s)));
  }, [experts, q, field]);

  return (
    <AppShell>
      <PageHeader eyebrow={t("emp.directory.eyebrow")} title={t("emp.directory.title")} description={t("emp.directory.subtitle")} />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("emp.directory.search")} className="h-11 rounded-lg pl-9" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <Chip active={field === "all"} onClick={() => setField("all")}>{t("emp.directory.all")}</Chip>
          {FIELDS.map((f) => <Chip key={f} active={field === f} onClick={() => setField(f)}>{f.split(" ")[0]}</Chip>)}
        </div>
      </div>

      {experts === null ? (
        <div className="grid place-items-center py-24"><Spinner className="text-primary w-7 h-7" /></div>
      ) : experts.length === 0 ? (
        <EmptyState icon={Users} title={t("emp.directory.emptyTitle")} description={t("emp.directory.emptyDesc")} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title={t("emp.directory.noMatches")} description={t("emp.directory.noMatchesDesc")} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((e, i) => <div key={e.expert_id} className={`animate-fade-up d${(i % 6) + 1}`}><ExpertCard expert={e} /></div>)}
        </div>
      )}
    </AppShell>
  );
}

function Chip({ active, children, onClick }) {
  return (
    <button onClick={onClick} className={`shrink-0 text-sm font-medium rounded-lg px-3 h-11 border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}`}>
      {children}
    </button>
  );
}
